'use client'
import { Grid } from '@achieve/ascend'
import { _get } from 'utils/shared'
import {
  MediaImageStandard,
  Typography,
  TypographyLongForm,
  TypographyString,
} from 'components/Contentful'
import ProviderReview from 'components/ProviderReview/ProviderReview'
import { useEffect, useRef, useState } from 'react'
import { BLOCKS } from '@contentful/rich-text-types'
import { ArticleTOC } from 'components/ArticleTOC'
import { Pill } from 'components/Pill'
import { removeSpecialCharacters } from 'utils/shared'
import { AchieveLink } from 'components/AchieveLink'
import { AUTHORS_ROUTE } from 'constants/navigation'
import ProviderCompare from 'components/ProviderCompare/ProviderCompare'
import { getPageContentTypes } from '@/lib/getPageContentType'
import { WEBSITE_NAMES } from '@/constants/websites'
import { AUTHOR_ROUTE } from '@/constants/navigation'
import { getArticlePeople } from 'lib/articlePeople'

const BillsArticlesContentTypes = {
  Review: 'Review',
  Evergreen: 'Evergreen',
  'Sub-Category': 'Sub-Category',
  Blog: 'Blog',
  Company: 'Company',
  Rate: 'Rate',
  Calculator: 'Calculator',
  Category: 'Category',
  'Press Releases': 'Press Releases',
  Best: 'Best',
}

function ArticleView({ content, siteInfo = {} }) {
  const { websiteField } = getPageContentTypes()
  const [tableOfContents, setTableOfContents] = useState([])
  const refs = useRef({})
  const articleContainerRef = useRef(null)
  const [activeSection, setActiveSection] = useState()
  const { authors, coauthors, reviewers } = getArticlePeople(
    _get(content.articleData, 'items[0].fields', {})
  )
  const articleAuthors = [
    ...authors.map((person) => ({ person, label: 'Written by' })),
    ...coauthors.map((person) => ({ person, label: 'Co-written by' })),
  ]
  const mainBody = _get(content.articleData, 'items[0].fields.mainBody')
  const primaryCategory = _get(content.articleData, 'items[0].fields.primaryCategory')
  const secondaryCategories = _get(content.articleData, 'items[0].fields.secondaryCategory')
  const contentType = _get(content.articleData, 'items[0].fields.contentType')
  const provider = _get(content.articleData, 'items[0].fields.provider.fields')
  const providerType = _get(content.articleData, 'items[0].fields.provider.sys.contentType.sys.id')
  const logoUrl = _get(content.articleData, 'items[0].fields.logoImage')
  const providerWebsiteLink = _get(content.articleData, 'items[0].fields.providerWebsiteLink')
  const rating = _get(content.articleData, 'items[0].fields.overallRating')
  const bestTitle = _get(content.articleData, 'items[0].fields.bestTitle')
  const description = _get(content.articleData, 'items[0].fields.description')
  const compareWith = _get(content.articleData, 'items[0].fields.compareWith')
  const authorBaseRoute = websiteField === WEBSITE_NAMES.BILLS ? AUTHOR_ROUTE : AUTHORS_ROUTE
  const isNotSimpleContentType = !Object.values(BillsArticlesContentTypes).includes(contentType)
  const showTableOfContents =
    websiteField === WEBSITE_NAMES.BILLS
      ? [BillsArticlesContentTypes.Blog, BillsArticlesContentTypes.Best].includes(contentType)
      : true
  let categories = [primaryCategory]
  if (secondaryCategories) {
    categories = categories.concat(secondaryCategories)
  }

  // useEffect to generate a table of content based on H2 elements found in mainBody
  useEffect(() => {
    const articles = []
    if (mainBody?.content) {
      for (let i = 0; i < mainBody.content.length; i++) {
        const element = mainBody.content[i]
        if (element.nodeType === BLOCKS.HEADING_2) {
          const sectionContent = _get(element, 'content[0].value') ?? ''
          articles.push({
            text: sectionContent,
            id: removeSpecialCharacters(
              `${BLOCKS.HEADING_2}-${sectionContent.slice(0, 50).replaceAll(' ', '-')}`
            ),
          })
        }
      }
    }
    setTableOfContents(articles)
  }, [mainBody])

  // useEffect to find the active section and marked it in the table of contents
  useEffect(() => {
    if (tableOfContents) {
      setActiveSection(tableOfContents?.[0]?.id)

      const container = articleContainerRef.current
      tableOfContents?.forEach(({ id }) => {
        refs.current[id] = container?.querySelector(`#${CSS.escape(id)}`) ?? null
      })

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) setActiveSection(entry.target.id)
          })
        },
        {
          root: null,
          rootMargin: '-30% 0px -30% 0px', // Adjust rootMargin to observe the middle of the page
          threshold: 0, // Threshold can be 0 to detect any intersection
        }
      )

      tableOfContents.forEach(({ id }) => {
        if (refs.current[id]) {
          observer.observe(refs.current[id])
        }
      })

      return () => {
        tableOfContents.forEach(({ id }) => {
          if (refs.current[id]) {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            observer.unobserve(refs.current[id])
          }
        })
      }
    }
  }, [tableOfContents])

  const renderReviewer = (typeText, slug, nameText, imageData, variant = '', withLink = false) => {
    const authorPageRoute = `${authorBaseRoute}/${slug}`
    return (
      <div className={'group flex flex-row min-w-[240px] gap-2'} data-variant={variant}>
        <div className={'flex items-center'}>
          {imageData ? (
            <MediaImageStandard
              className={'rounded-lg'}
              alt={imageData?.fileName}
              content={imageData}
              layout="fixed"
              width={40}
              height={40}
              fetchPriority="high"
              preload={true}
              objectFit="cover"
            />
          ) : (
            <div className={'w-10 h-10 bg-neutral-600 rounded-lg'} />
          )}
        </div>
        <div className={'flex flex-col'}>
          <div
            className={
              '[&_p:last-child]:text-neutral-300 [&_p:last-child]:mb-0 group-data-[variant="title"]:[&_p:last-child]:text-black'
            }
            data-testid="article-view-reviewer-type"
          >
            <TypographyLongForm content={typeText} variant="bodySm" />
          </div>
          <div
            className={
              '[&_a]:text-neutral-300 [&_p]:font-normal [&_p]:mb-0 [&_a:hover>p]:text-blue-400'
            }
            data-testid="article-view-reviewer-name"
          >
            {withLink ? (
              <AchieveLink
                track={{
                  list_name: 'AUTHOR VIEW PROFILE LINK',
                  click_id: 'View Profile',
                  click_text: `${siteInfo?.mainProductHyphen} | View Profile`,
                }}
                href={authorPageRoute}
                data-testid="author-view-profile-link"
              >
                <TypographyString content={nameText} variant="labelSm" />
              </AchieveLink>
            ) : (
              <TypographyString content={nameText} variant="labelSm" />
            )}
          </div>
        </div>
      </div>
    )
  }

  const renderReviewerCard = (typeText, slug, nameText, summary, imageData, withLink) => {
    return (
      <div
        className={
          'flex flex-col w-full gap-2 bg-neutral-50 h-full border p-6 rounded-md border-solid border-neutral-50'
        }
        data-testid="article-view-reviewer-card"
      >
        {renderReviewer(typeText, slug, nameText, imageData, null, withLink)}
        <div className={'[&_p]:last:mb-0'}>
          <TypographyLongForm
            content={summary}
            variant="bodyXs"
            data-testid="article-view-reviewer-summary"
          />
        </div>
      </div>
    )
  }

  return (
    <>
      <Grid size={{ xs: 12 }} ref={articleContainerRef}>
        <Grid container spacing={2}>
          {tableOfContents?.length >= 1 && (isNotSimpleContentType || showTableOfContents) && (
            <Grid item size={{ xs: 12, lg: 3 }}>
              <div className={'sticky mb-8 p-0 top-24'}>
                <ArticleTOC
                  content={tableOfContents}
                  withTitle={false}
                  variantItem="labelSm"
                  activeSection={activeSection}
                />
              </div>
            </Grid>
          )}
          <Grid
            item
            size={{
              xs: 12,
              lg:
                tableOfContents?.length >= 1 && (isNotSimpleContentType || showTableOfContents)
                  ? 9
                  : 12,
            }}
            className={''}
          >
            {provider && (
              <ProviderReview
                providerType={providerType}
                providerData={provider}
                logoUrl={logoUrl}
                providerWebsiteLink={providerWebsiteLink}
                rating={rating}
                bestTitle={bestTitle}
                description={description}
              />
            )}
            <TypographyLongForm
              content={mainBody}
              variantOverride={{
                [BLOCKS.HEADING_2]: 'headingMd',
                [BLOCKS.PARAGRAPH]: 'bodyMd',
              }}
            />
            {compareWith?.length > 0 && <ProviderCompare items={compareWith} />}
          </Grid>
          {(articleAuthors.length > 0 || reviewers.length > 0) && (
            <div data-testid="article-view-author-wrapper">
              <Typography className={'mb-4'} content={'Author Information'} variant="headingMd" />
              <div
                className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-1"
                data-testid="article-view-author-section"
              >
                {articleAuthors.length > 0 && (
                  <div className="flex flex-wrap gap-4" data-testid="article-view-author">
                    {articleAuthors.map(({ person, label }, index) => (
                      <div
                        className="min-w-[240px] flex-[1_1_240px]"
                        key={person.sys?.id ?? person.fields.slug ?? index}
                      >
                        {renderReviewerCard(
                          label,
                          person.fields.slug,
                          person.fields.authorName,
                          person.fields.authorByline,
                          person.fields.authorPhoto,
                          true
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {reviewers.length > 0 && (
                  <div className="flex flex-wrap gap-4" data-testid="article-view-reviewer">
                    {reviewers.map((reviewer, index) => (
                      <div
                        className="min-w-[240px] flex-[1_1_240px]"
                        key={reviewer.sys?.id ?? reviewer.fields.slug ?? index}
                      >
                        {renderReviewerCard(
                          reviewer.roleLabel ?? 'Reviewed by',
                          reviewer.fields.slug,
                          reviewer.fields.authorName,
                          reviewer.fields.authorByline,
                          reviewer.fields.authorPhoto,
                          true
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          {isNotSimpleContentType && (
            <Grid item size={{ xs: 12 }}>
              <Grid container className={''} data-testid="article-view-categories-wrapper">
                {categories.map((category, idx) => {
                  return (
                    <Pill
                      key={idx}
                      className={'text-neutral-900 text-[25px]'}
                      category={category}
                      id={idx}
                    />
                  )
                })}
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
    </>
  )
}

export { ArticleView }
